import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Database, Robot, Buildings, BookOpen, Sparkle, ArrowRight, ClockCounterClockwise, Plus,
} from "@phosphor-icons/react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut,
} from "@/components/ui/command";
import { useAuth } from "@/context/AuthContext";
import { useGlobalSearch } from "@/lib/queries";
import { ALL_NAV_ITEMS } from "@/lib/nav";
import { ASSISTANTS } from "@/lib/assistants";
import { ASSISTANT_MODE_ITEMS } from "@/lib/assistantModes";
import { getRecents } from "@/lib/recents";

// Build a deep-link path from a server search result (section → ?tab=).
const pathOf = (it) => (it.section ? `${it.target}?tab=${it.section}` : it.target);

const TYPE_META = {
  workspaces: { heading: "Clients", icon: Buildings, iconCls: "text-primary", testId: "cmd-search-workspace" },
  documents: { heading: "Knowledge", icon: Database, iconCls: "text-muted-foreground", testId: "cmd-search-doc" },
  outputs: { heading: "AI Outputs", icon: Robot, iconCls: "text-muted-foreground", testId: "cmd-search-output" },
  library: { heading: "Content Library", icon: BookOpen, iconCls: "text-muted-foreground", testId: "cmd-search-library" },
  intelligence: { heading: "Global Intelligence", icon: Sparkle, iconCls: "text-accent-ai", testId: "cmd-search-intel" },
};
const GROUP_ORDER = ["workspaces", "documents", "outputs", "library", "intelligence"];

const RECENT_ICON = { workspace: Buildings, output: Robot };

function ResultRow({ it, Icon, iconCls, testId, keep, go }) {
  return (
    <CommandItem value={keep(`${it.type} ${it.title} ${it.id}`)} onSelect={() => go(pathOf(it))} data-testid={testId}>
      <Icon className={`h-4 w-4 shrink-0 ${iconCls}`} weight="bold" />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{it.title}</span>
        {it.snippet && <span className="block truncate text-xs text-muted-foreground">{it.snippet}</span>}
      </span>
      {it.badge && <CommandShortcut className="capitalize">{it.badge}</CommandShortcut>}
    </CommandItem>
  );
}

function SearchResults({ results, keep, go }) {
  return GROUP_ORDER.map((key) => {
    const items = results[key] || [];
    if (items.length === 0) return null;
    const meta = TYPE_META[key];
    return (
      <CommandGroup key={key} heading={meta.heading}>
        {items.map((it) => (
          <ResultRow key={`${key}-${it.id}`} it={it} Icon={meta.icon} iconCls={meta.iconCls} testId={meta.testId} keep={keep} go={go} />
        ))}
      </CommandGroup>
    );
  });
}

function RecentsGroup({ recents, go }) {
  if (recents.length === 0) return null;
  return (
    <CommandGroup heading="Recently viewed">
      {recents.map((r) => {
        const Icon = RECENT_ICON[r.type] || ClockCounterClockwise;
        return (
          <CommandItem key={`recent-${r.type}-${r.id}`} value={`recent ${r.title}`} onSelect={() => go(r.path)} data-testid="cmd-recent">
            <Icon className="h-4 w-4 text-muted-foreground" weight="bold" />
            <span className="truncate">{r.title}</span>
            {r.badge && <CommandShortcut className="capitalize">{r.badge}</CommandShortcut>}
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}

function NavGroup({ navItems, go }) {
  return (
    <CommandGroup heading="Go to">
      {navItems.map((i) => (
        <CommandItem key={i.key} value={`nav ${i.label}`} onSelect={() => go(i.to)} data-testid={`cmd-nav-${i.key}`}>
          <i.icon className="h-4 w-4 text-muted-foreground" weight="bold" />
          <span>{i.label}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function AssistantGroup({ can, assistantActions, go }) {
  return (
    <CommandGroup heading="Generate with an assistant">
      {(can("proposal:use") || can("grc:use") || can("knowledge:upload")) && (
        <CommandItem value="action new client workspace" onSelect={() => go("/workspaces")} data-testid="cmd-action-new-workspace">
          <Plus className="h-4 w-4 text-accent-ai" weight="bold" />
          <span>New client workspace</span>
          <CommandShortcut><ArrowRight className="h-3.5 w-3.5" /></CommandShortcut>
        </CommandItem>
      )}
      {assistantActions.map((a) => (
        <CommandItem key={a.key} value={`generate ${a.label}`} onSelect={() => go(`/studio?assistant=${a.key}`)} data-testid={`cmd-assistant-${a.key}`}>
          <a.icon className="h-4 w-4 text-accent-ai" weight="bold" />
          <span>{a.label}</span>
          <CommandShortcut><ArrowRight className="h-3.5 w-3.5" /></CommandShortcut>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function AssistantModesGroup({ can, go }) {
  const items = ASSISTANT_MODE_ITEMS.filter((m) => can(m.perm));
  if (items.length === 0) return null;
  return (
    <CommandGroup heading="AI Assistant Modes">
      {items.map((m) => (
        <CommandItem key={m.id} value={m.keywords} onSelect={() => go(m.to)} data-testid={`cmdk-result-${m.id}`}>
          <m.icon className="h-4 w-4 shrink-0 text-accent-ai" weight="bold" />
          <span className="min-w-0 flex-1 truncate text-sm">
            <span className="text-muted-foreground">{m.assistantLabel}</span>
            <span className="px-1 text-muted-foreground/50">›</span>
            <span className="text-foreground">{m.label}</span>
          </span>
          <CommandShortcut><ArrowRight className="h-3.5 w-3.5" /></CommandShortcut>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [query, setQuery] = useState("");
  const searching = query.trim().length >= 2;

  const { data: results, isFetching } = useGlobalSearch(searching ? query.trim() : "");

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const go = (to) => { onOpenChange(false); navigate(to); };
  // Force server-matched results to always pass cmdk's client filter.
  const keep = (label) => `${query} ${label}`;

  const navItems = ALL_NAV_ITEMS.filter((i) => can(i.perm));
  const assistantActions = ASSISTANTS.filter((a) => can(a.perm));
  const recents = open && !searching ? getRecents() : [];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput value={query} onValueChange={setQuery}
        placeholder="Search clients, knowledge, outputs, intelligence — or run an action…" data-testid="command-input" />
      <CommandList>
        <CommandEmpty>{searching ? (isFetching ? "Searching…" : "No matching results found.") : "Start typing to search."}</CommandEmpty>

        {!searching && <RecentsGroup recents={recents} go={go} />}

        {/* Nav + assistants stay filterable while typing so ⌘K remains a keyboard navigator */}
        <NavGroup navItems={navItems} go={go} />

        {searching && results && <SearchResults results={results} keep={keep} go={go} />}

        {searching && <AssistantModesGroup can={can} go={go} />}

        <AssistantGroup can={can} assistantActions={assistantActions} go={go} />
      </CommandList>
    </CommandDialog>
  );
}

/** Global ⌘K / Ctrl-K listener — call with a setter to toggle the palette. */
export function useCommandPaletteShortcut(setOpen) {
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setOpen]);
}
